import { LANDING_OFFER_FROM_META_KEY, LANDING_BRANDED_OFFER_FROM_META, COMPANY_HOSTNAME } from "../constants";
import localstory from "localstory";
import { warning } from "./flog";
import { getCookie } from "./cookies";
import { getMarketingTrackingVariable } from "@flights/web-api-utils-universal";

import { MarketingTrackingVariables } from "@flights/types";
import {
  FLYGRESOR_EXT_ORIGIN_PARAM_VALUES,
  GOOGLE_FLIGHTS_EXT_ORIGIN_PARAM_VALUES,
  GOOGLE_SEARCH_WIDGET_EXT_ORIGIN_PARAM_VALUES,
  KAYAK_EXT_ORIGIN_PARAM_VALUES,
  SKYSCANNER_EXT_ORIGIN_PARAM_VALUES,
  WEGO_EXT_ORIGIN_PARAM_VALUES
} from "@flights/constants";

const isClient = process.env.BUILD_TARGET === "client";
const sessionStore = isClient && localstory(window.sessionStorage, "flights");

export const getExtOriginMarketingTrackingVariable = () => {
  if (isClient) {
    return sessionStore && sessionStore.get("ext-origin");
  }
  return window?.__GLOBAL_CONTEXT__?.marketingTrackingVariables?.externalOrigin;
};

export const getSearchApiMarketingParams = () => {
  const mappedParams = {};
  const externalOrigin = getMarketingTrackingVariable("ext-origin");
  const externalForward = getMarketingTrackingVariable("ext-fwd");
  const salesCountry = getMarketingTrackingVariable("salesCountry");

  //https://drive.google.com/file/d/140HE2DVKqLr0uqvKLw1vluKiNxt9b0bP/view
  if (externalOrigin) {
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    mappedParams["salesChannel"] = externalOrigin;
  }
  if (externalForward) {
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    mappedParams["salesIdentifier"] = externalForward;
  }
  if (salesCountry) {
    // @ts-expect-error: Element implicitly has an 'any' type. Fix the issue timely.
    mappedParams["salesCountry"] = salesCountry;
  }

  return mappedParams;
};

export const setMarketingTrackingVariables = (marketingTrackingVariables: MarketingTrackingVariables | undefined) => {
  //all variables are stored in sessionStore
  if (!isClient || !sessionStore) {
    return;
  }

  marketingTrackingVariables?.aid && sessionStore.set("aid", marketingTrackingVariables.aid);
  marketingTrackingVariables?.label && sessionStore.set("label", marketingTrackingVariables.label);
  marketingTrackingVariables?.gclid && sessionStore.set("gclid", marketingTrackingVariables.gclid);
  marketingTrackingVariables?.auid && sessionStore.set("auid", marketingTrackingVariables.auid);
  marketingTrackingVariables?.adPlat && sessionStore.set("adplat", marketingTrackingVariables.adPlat);
  if (!marketingTrackingVariables?.adPlat) {
    try {
      const crossSellAdplat = getCookie("cross_sell_adplat_FLIGHT");
      if (crossSellAdplat) {
        sessionStore.set("adplat", crossSellAdplat);
      }
    } catch (error) {
      warning(`Cross sell adplat failed to be set from localStorage\n Error: ${error.message}`);
    }
  }
  marketingTrackingVariables?.externalCountry &&
    sessionStore.set("salesCountry", marketingTrackingVariables.externalCountry);
  marketingTrackingVariables?.externalCurrency &&
    sessionStore.set("salesCurrency", marketingTrackingVariables.externalCurrency);
  marketingTrackingVariables?.externalOrigin &&
    sessionStore.set("ext-origin", marketingTrackingVariables.externalOrigin);
  marketingTrackingVariables?.externalForward &&
    sessionStore.set("ext-fwd", marketingTrackingVariables.externalForward);
  marketingTrackingVariables?.externalSource && sessionStore.set("ext-src", marketingTrackingVariables.externalSource);
  marketingTrackingVariables?.externalTracking &&
    sessionStore.set("ext-tr", marketingTrackingVariables.externalTracking);
  marketingTrackingVariables?.distributionId &&
    sessionStore.set("distribution_id", marketingTrackingVariables.distributionId);
};

export const isOfMetaOrigin = () => {
  if (window.location.host === COMPANY_HOSTNAME) {
    return !!window.__GLOBAL_CONTEXT__.isOfMetaOrigin;
  }

  return !!getExtOriginMarketingTrackingVariable();
};
export const isOfPPCOrigin = () => {
  return !!window?.__GLOBAL_CONTEXT__.isPPC;
};
export const isDirect = () => !isOfMetaOrigin() && !isOfPPCOrigin();

export const isOfGoogleFlightsOrigin = () =>
  GOOGLE_FLIGHTS_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());
export const isOfKayakOrigin = () => KAYAK_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());
export const isOfSkyScannerOrigin = () =>
  SKYSCANNER_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());
export const isOfWegoOrigin = () => WEGO_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());
export const isOfFlygresorOrigin = () =>
  FLYGRESOR_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());
export const isOfGoogleSearchWidgetOrigin = () =>
  GOOGLE_SEARCH_WIDGET_EXT_ORIGIN_PARAM_VALUES.includes(getExtOriginMarketingTrackingVariable());

export const getMetaLandingOfferToken = () => {
  if (!sessionStore) {
    return;
  }
  return sessionStore.get(LANDING_OFFER_FROM_META_KEY);
};

export const isMetaLandingOfferToken = (offerToken: string) => {
  if (!sessionStore) {
    return;
  }
  const landingOfferToken = getMetaLandingOfferToken();
  return landingOfferToken && offerToken.includes(landingOfferToken);
};

export const isMetaLandingBrandedOfferToken = (brandedOfferToken: string) => {
  if (!sessionStore) {
    return;
  }
  const landingOfferToken = getMetaLandingOfferToken();
  const landingBrandedOfferToken = sessionStore.get(LANDING_BRANDED_OFFER_FROM_META);
  return landingOfferToken && brandedOfferToken == landingBrandedOfferToken;
};

//offerToken : main offer token, brandedOfferToken: offer token off the branded fare offer
export function setLandingBrandedFareOfferToken(offerToken: string, brandedOfferToken: string) {
  if (!sessionStore) {
    return;
  }
  const isMeta = isOfMetaOrigin();
  const isLandingOfferToken = isMetaLandingOfferToken(offerToken);

  //offerToken can be a normal offer token or a previously set branded offer token
  const isLandingBrandedOfferToken = isMetaLandingBrandedOfferToken(offerToken);

  if (isMeta && (isLandingOfferToken || isLandingBrandedOfferToken)) {
    sessionStore.set(LANDING_BRANDED_OFFER_FROM_META, brandedOfferToken);
  }
}
